import { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Paperclip, Download, AlertCircle, Mail, MailOpen, CheckCircle2, Loader2, FolderInput, MonitorDown } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API } from '../../config';
import { DeliveryPill } from './DeliveryPill';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useAuth } from '../../contexts/AuthContext';
import DocsModal from '../DocsModal';

/**
 * Authenticated attachment chip. Clicking opens a small two-option popover:
 *   • Save in claim — fetches the bytes, opens DocsModal, and feeds the file
 *     into the normal upload flow (user ticks doc-type + Save).
 *   • Download — same axios.get(blob) → trigger in-tab download we had
 *     before. Cannot use `<a target="_blank">` because the new browser tab
 *     doesn't inherit sessionStorage Bearer tokens or our axios
 *     X-Operator-Tenant interceptor header, so cross-tenant operators 404'd.
 *
 * "Save in claim" is hidden for viewers since they can't upload anyway —
 * showing the option would just dead-end at the DocsModal permission check.
 */
const AttachmentChip = ({ msg, att, idx, claim, onSaveToClaim, saved }) => {
  const { user } = useAuth();
  const canSaveToClaim = user?.role && user.role !== 'viewer' && claim?.report_no;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const fetchBlob = async () => {
    const r = await axios.get(
      `${API}/claim-emails/${msg.email_id}/attachments/${idx}`,
      { responseType: 'blob' },
    );
    return r.data;
  };

  const handleDownload = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    setPopoverOpen(false);
    try {
      const blob = await fetchBlob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = att.name || `attachment-${idx}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
    } catch (ex) {
      const detail = ex?.response?.data?.detail || ex?.message || 'Download failed';
      setErr(typeof detail === 'string' ? detail : 'Download failed');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveInClaim = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    setPopoverOpen(false);
    try {
      const blob = await fetchBlob();
      const file = new File([blob], att.name || `attachment-${idx}`, {
        type: att.content_type || blob.type || 'application/octet-stream',
      });
      // Hand the file + this attachment's idx up to the modal so it can
      // mount DocsModal AND record (post-upload) which idx was saved.
      onSaveToClaim?.({ files: [file], indexes: [idx] });
    } catch (ex) {
      const detail = ex?.response?.data?.detail || ex?.message || 'Could not load attachment';
      setErr(typeof detail === 'string' ? detail : 'Could not load attachment');
      toast.error(typeof detail === 'string' ? detail : 'Could not load attachment');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inline-flex flex-col">
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={busy}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
              saved
                ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
            data-testid={`thread-att-${msg.email_id}-${idx}`}
            title={saved ? `${att.name} — saved to claim` : att.name}
          >
            <Paperclip className="w-3 h-3" />
            <span className="max-w-[160px] truncate">{att.name}</span>
            {saved
              ? <CheckCircle2 className="w-3 h-3 text-emerald-600" data-testid={`thread-att-${msg.email_id}-${idx}-saved-tick`} />
              : (busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3 text-gray-500" />)}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          /* z-[70] keeps the attachment menu above EmailThreadModal (z-58)
           * and EmailComposeModal (z-55). Radix Popover defaults to z-50
           * which renders BEHIND the modal — the user reported the
           * "Download to computer" option appearing under the dialog. */
          className="w-52 p-1 z-[70]"
          data-testid={`thread-att-${msg.email_id}-${idx}-menu`}
        >
          {canSaveToClaim && (
            <button
              type="button"
              onClick={handleSaveInClaim}
              className="w-full flex items-center gap-2 px-2.5 py-1.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 rounded transition-colors text-left"
              data-testid={`thread-att-${msg.email_id}-${idx}-save-claim`}
            >
              <FolderInput className="w-4 h-4 text-teal-500" />
              <span>{saved ? 'Save in claim again' : 'Save in claim'}</span>
            </button>
          )}
          <button
            type="button"
            onClick={handleDownload}
            className="w-full flex items-center gap-2 px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors text-left"
            data-testid={`thread-att-${msg.email_id}-${idx}-download`}
          >
            <MonitorDown className="w-4 h-4 text-gray-500" />
            <span>Download to computer</span>
          </button>
        </PopoverContent>
      </Popover>
      {err && <span className="text-[10px] text-red-500 mt-0.5">{err}</span>}
    </div>
  );
};

/** Single-message card used inside the thread view. Visually mirrors the
 *  Emails-tab row colours: teal-tinted for outbound (sent), blue-tinted for
 *  inbound (received), red for delivery failures. */
const MessageCard = ({ msg, defaultExpanded, onReenriched, claim, onSaveAttachmentToClaim }) => {
  const isInbound = msg.direction === 'received';
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState(null);

  const handleRetry = async (e) => {
    e?.stopPropagation();
    if (retrying) return;
    setRetrying(true);
    setRetryError(null);
    try {
      const r = await axios.post(`${API}/email/inbound/${msg.email_id}/reenrich`);
      if (r.data?.email) onReenriched?.(r.data.email);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Retry failed';
      setRetryError(typeof detail === 'string' ? detail : 'Retry failed');
    } finally {
      setRetrying(false);
    }
  };
  // Quick lookup for which attachment indexes are already saved to the
  // claim's Documents. Persisted by the backend via $addToSet so the order
  // doesn't matter — we just need O(1) membership checks below.
  const savedSet = new Set(msg.saved_attachment_indexes || []);
  const hasMultipleAttachments = (msg.attachments?.length || 0) >= 2;
  const unsavedIndexes = (msg.attachments || [])
    .map((_, i) => i)
    .filter((i) => !savedSet.has(i));
  const [bulkBusy, setBulkBusy] = useState(false);
  const handleSaveAllToClaim = async () => {
    if (bulkBusy || !claim?.report_no || unsavedIndexes.length === 0) return;
    setBulkBusy(true);
    try {
      // Fetch each unsaved attachment as a blob in parallel — small batch
      // (typically <5 files) so we don't bother queueing.
      const results = await Promise.all(unsavedIndexes.map(async (i) => {
        const att = msg.attachments[i];
        const r = await axios.get(
          `${API}/claim-emails/${msg.email_id}/attachments/${i}`,
          { responseType: 'blob' },
        );
        return new File([r.data], att.name || `attachment-${i}`, {
          type: att.content_type || r.data.type || 'application/octet-stream',
        });
      }));
      onSaveAttachmentToClaim?.({ files: results, indexes: unsavedIndexes });
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || ex?.message || 'Could not load attachments');
    } finally {
      setBulkBusy(false);
    }
  };
  return (
    <div
      className={`rounded-lg border ${
        isInbound ? 'bg-blue-50/60 border-blue-200' : 'bg-white border-gray-200'
      }`}
      data-testid={`thread-msg-${msg.email_id}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-start gap-3 px-4 py-3 text-left"
      >
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isInbound ? 'bg-blue-100' : 'bg-teal-100'
        }`}>
          {msg.status === 'failed' ? (
            <AlertCircle className="w-4 h-4 text-red-500" />
          ) : isInbound ? (
            <Mail className="w-4 h-4 text-blue-600" style={{ transform: 'scaleX(-1)' }} />
          ) : msg.status === 'sent' ? (
            <CheckCircle2 className="w-4 h-4 text-teal-600" />
          ) : (
            <MailOpen className="w-4 h-4 text-teal-600" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 mb-0.5">
            <span className="text-sm font-semibold text-gray-800 truncate">
              {isInbound ? msg.from : 'You'}
              <span className="ml-2 font-normal text-gray-500">
                {isInbound ? `→ ${(msg.to || []).slice(0, 2).join(', ')}${(msg.to || []).length > 2 ? '…' : ''}` : `→ ${(msg.to || []).slice(0, 2).join(', ')}${(msg.to || []).length > 2 ? '…' : ''}`}
              </span>
            </span>
            <span className="text-[11px] text-gray-400 flex-shrink-0">
              {msg.sent_at ? new Date(msg.sent_at).toLocaleString() : (msg.created_at ? new Date(msg.created_at).toLocaleString() : '')}
            </span>
          </div>
          {!expanded && msg.body_preview && (
            <p className="text-xs text-gray-500 truncate">{msg.body_preview}</p>
          )}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {msg.attachments?.length > 0 && !expanded && (
              <span className="text-[11px] text-gray-400 inline-flex items-center gap-1">
                <Paperclip className="w-3 h-3" />
                {msg.attachments.length}
              </span>
            )}
            {/* "N saved to claim" badge — gives the user instant feedback
                that some attachments are already linked to the claim's
                Documents, so they don't re-save the same file by mistake. */}
            {savedSet.size > 0 && (
              <span
                className="text-[10px] uppercase tracking-wide font-semibold inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200"
                data-testid={`thread-msg-${msg.email_id}-saved-badge`}
                title={`${savedSet.size} attachment${savedSet.size === 1 ? '' : 's'} saved to claim documents`}
              >
                <CheckCircle2 className="w-3 h-3" />
                {savedSet.size} saved
              </span>
            )}
            {/* Engagement pill for outbound messages — renders nothing for received. */}
            <DeliveryPill msg={msg} />
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-gray-100">
          {/* CC / BCC if present */}
          {(msg.cc?.length > 0 || msg.bcc?.length > 0) && (
            <p className="text-[11px] text-gray-400 mb-2">
              {msg.cc?.length > 0 && <>Cc: {msg.cc.join(', ')}{msg.bcc?.length > 0 ? ' · ' : ''}</>}
              {msg.bcc?.length > 0 && <>Bcc: {msg.bcc.join(', ')}</>}
            </p>
          )}
          {(msg.body_html || msg.body_text) ? (
            <div
              className="prose prose-sm max-w-none text-gray-700"
              dangerouslySetInnerHTML={{ __html: msg.body_html || (msg.body_text || '').replace(/\n/g, '<br/>') }}
            />
          ) : (
            // Inbound row whose body fetch hasn't landed yet (or one inserted
            // under the old code that wrongly read body inline). Offer a
            // manual retry button — calls the re-enrich endpoint which hits
            // Resend's Receiving API and fills the placeholder.
            <div data-testid={`msg-empty-${msg.email_id}`}>
              <p className="text-xs text-gray-400 italic">
                {msg.direction === 'received'
                  ? (msg.enrichment_status === 'pending'
                      ? 'Fetching body from Resend… (this can take a few seconds)'
                      : 'Body not captured for this message yet.')
                  : 'No body content.'}
              </p>
              {msg.direction === 'received' && msg.resend_message_id && (
                <button
                  type="button"
                  onClick={handleRetry}
                  disabled={retrying}
                  className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed rounded text-xs text-blue-700 transition-colors"
                  data-testid={`msg-retry-${msg.email_id}`}
                >
                  {retrying ? 'Retrying…' : 'Retry fetch'}
                </button>
              )}
              {retryError && (
                <p className="mt-1 text-[11px] text-red-500">{retryError}</p>
              )}
            </div>
          )}
          {msg.attachments?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              {/* Bulk action — only meaningful when there are 2+ attachments
                  AND at least one isn't already saved. Lets users move
                  multiple files into the claim's Documents in one click. */}
              {hasMultipleAttachments && unsavedIndexes.length >= 2 && claim?.report_no && (
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] text-gray-500">
                    {unsavedIndexes.length} of {msg.attachments.length} not yet saved
                  </span>
                  <button
                    type="button"
                    onClick={handleSaveAllToClaim}
                    disabled={bulkBusy}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-teal-50 hover:bg-teal-100 disabled:opacity-60 disabled:cursor-not-allowed rounded text-xs font-medium text-teal-700 border border-teal-200 transition-colors"
                    data-testid={`thread-msg-${msg.email_id}-save-all-to-claim`}
                  >
                    {bulkBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FolderInput className="w-3 h-3" />}
                    Save all to claim
                  </button>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {msg.attachments.map((a, i) => (
                  <AttachmentChip
                    key={i}
                    msg={msg}
                    att={a}
                    idx={i}
                    claim={claim}
                    saved={savedSet.has(i)}
                    onSaveToClaim={onSaveAttachmentToClaim}
                  />
                ))}
              </div>
            </div>
          )}
          {msg.status === 'failed' && (
            <p className="mt-2 flex items-center gap-1 text-xs text-red-600">
              <AlertCircle className="w-3.5 h-3.5" /> Failed to deliver{msg.error ? ` — ${msg.error}` : ''}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/** Read-only viewer that adapts to either a single-message view (legacy
 *  `emailId` prop) or a full thread view (`threadToken` prop). When the
 *  thread fetch returns multiple messages they stack newest-at-the-bottom
 *  like a real mailbox; the last message starts expanded, older ones are
 *  collapsed to a preview line so users can quickly scan history. */
const EmailThreadModal = ({ open, onClose, emailId, threadToken, onReply, claim }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // When user picks "Save in claim" on an attachment we fetch the bytes as
  // a File and stash it here along with the source email_id + idx list.
  // The DocsModal mount below opens whenever this is non-null and feeds
  // the file(s) into its existing upload pipeline. Once the upload
  // succeeds we POST /saved-attachments to record which indexes are now
  // linked to the claim's Documents so the badge / chip-checkmarks render.
  const [pendingSave, setPendingSave] = useState(null);  // { files, indexes, email_id }
  const handleAttachmentToClaim = (sourceMsg) => ({ files, indexes }) => {
    setPendingSave({ files, indexes, email_id: sourceMsg.email_id });
  };
  const markSavedOnBackend = async (files) => {
    if (!pendingSave) return;
    try {
      const r = await axios.post(
        `${API}/claim-emails/${pendingSave.email_id}/saved-attachments`,
        { indexes: pendingSave.indexes },
      );
      const updated = r.data?.saved_attachment_indexes || [];
      // Update the in-memory thread so the saved badge / chip checkmark
      // refreshes without a round-trip to /claim-emails.
      setData((prev) => prev && ({
        ...prev,
        messages: prev.messages.map((m) =>
          m.email_id === pendingSave.email_id ? { ...m, saved_attachment_indexes: updated } : m
        ),
      }));
    } catch (e) {
      // Don't toast — the file uploaded successfully, just the bookkeeping
      // failed. The user will see the file in Docs but no badge until they
      // refresh. Logged for diagnostics.
      console.warn('Could not mark attachment as saved-to-claim:', e?.response?.status, e?.response?.data);
    }
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setData(null);
    const url = threadToken
      ? `${API}/claim-emails/thread/${encodeURIComponent(threadToken)}`
      : emailId
        ? `${API}/claim-emails/${emailId}`
        : null;
    if (!url) { setLoading(false); return; }
    axios.get(url)
      .then((r) => {
        if (threadToken) {
          setData(r.data); // { messages, count, subject_root, report_no }
        } else {
          // Wrap a single email into the thread-shape for unified rendering.
          setData({ messages: [r.data], count: 1, subject_root: r.data.subject, report_no: r.data.report_no });
        }
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, emailId, threadToken]);

  if (!open) return null;

  const messages = data?.messages || [];
  const lastIndex = messages.length - 1;
  const hasReceived = messages.some((m) => m.direction === 'received');

  return (
    <div
      className="fixed inset-0 z-[58] flex items-end md:items-center justify-center bg-black/50"
      onClick={(e) => {
        // Only close when the click was on the backdrop itself, NOT a child.
        // Without this guard, ANY click inside the modal (including children
        // that render via React portals like the DocsModal Radix Dialog) bubbles
        // here and closes us — the user-visible symptom was DocsModal appearing
        // to "immediately close" the moment any checkbox or button was clicked.
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="email-thread-modal"
    >
      <div
        className="bg-white w-full md:w-[760px] md:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col"
        style={{ maxHeight: '92vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900 truncate">
              {loading ? 'Loading…' : (data?.subject_root || 'Email')}
            </h3>
            {data?.count > 1 && (
              <p className="text-xs text-gray-500 mt-0.5">{data.count} messages · Claim {data.report_no}</p>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100" data-testid="email-thread-close">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2.5 bg-gray-50/40">
          {loading ? (
            <p className="text-center text-gray-400 py-8">Loading…</p>
          ) : messages.length === 0 ? (
            <p className="text-center text-gray-400 py-8">Email not found.</p>
          ) : (
            messages.map((m, i) => (
              <MessageCard
                key={m.email_id}
                msg={m}
                /* Newest message always expanded; older collapsed. With a single
                 * message thread (the legacy single-email case) the only message
                 * is the "newest" so it expands automatically. */
                defaultExpanded={i === lastIndex || messages.length === 1}
                claim={claim}
                /* Curry the handler with this message so the chip/bulk button
                 * doesn't need to know which message it belongs to — that
                 * context is captured at render time. */
                onSaveAttachmentToClaim={handleAttachmentToClaim(m)}
                onReenriched={(updated) => {
                  setData((prev) => prev && ({
                    ...prev,
                    messages: prev.messages.map((x) => x.email_id === updated.email_id ? { ...x, ...updated } : x),
                  }));
                }}
              />
            ))
          )}
        </div>

        {/* Reply affordance — only meaningful when at least one received
         *  message exists in the thread; otherwise it's just a re-send. */}
        {onReply && messages.length > 0 && hasReceived && (
          <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
            <button
              onClick={() => {
                const lastReceived = [...messages].reverse().find((m) => m.direction === 'received');
                onReply({
                  to: lastReceived?.from || '',
                  subject: data?.subject_root ? `Re: ${data.subject_root}` : '',
                });
                onClose?.();
              }}
              className="px-4 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded transition-colors"
              data-testid="thread-reply-btn"
            >
              Reply
            </button>
          </div>
        )}
      </div>
      {/* Mounted alongside the thread modal — DocsModal stacks on top via
          its own shadcn Dialog z-index so the user can save an attachment
          without losing their place in the email thread. Closes when the
          user finishes their save flow inside DocsModal. */}
      {claim && (
        <DocsModal
          open={!!pendingSave}
          onClose={() => setPendingSave(null)}
          claim={claim}
          initialFiles={pendingSave?.files || null}
          onUploadSuccess={markSavedOnBackend}
        />
      )}
    </div>
  );
};

export default EmailThreadModal;
